import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { getLocalValue, LoacalVariables, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { ORDER_COLORS as COLORS, orderFieldSx as fieldSx } from "../../../Components/Orders/OrderCardParts";
import {
  createStaff,
  deactivateStaff,
  fetchStaff,
  fetchStaffModules,
  getErrorMessage,
  StaffMember,
  StaffModule,
  updateStaffPermissions,
} from "./staffApi";

const NAME_MAX = 50;
// Indian mobile number: 10 digits starting 6-9
const PHONE_PATTERN = /^[6-9]\d{9}$/;

const checkboxSx = { color: COLORS.border, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: COLORS.primary } };
const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: COLORS.primary },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: COLORS.primary },
};
const primaryButtonSx = {
  bgcolor: COLORS.primary,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  "&:hover": { bgcolor: COLORS.dark },
};

// Every module, on or off, for the permissions request
const toPermissionList = (modules: StaffModule[], enabled: Record<string, boolean>) =>
  modules.map((m) => ({ moduleName: m.name, isEnabled: !!enabled[m.name] }));

const enabledCount = (modules: StaffModule[], enabled: Record<string, boolean>) =>
  modules.filter((m) => enabled[m.name]).length;

const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(-10);

type PendingConfirm = { kind: "discard"; proceed: () => void } | { kind: "deactivate" } | null;

// One page for both lab and pharmacy: staff on the left, the selected person's module access on the right
export const StaffPermissions: React.FC = () => {
  const { showMessage } = useSnackbar();
  const isPharmacy = getLocalValue(LoacalVariables.UserType) === "pharmacy";

  const [modules, setModules] = useState<StaffModule[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<PendingConfirm>(null);

  // Add staff dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newModules, setNewModules] = useState<Record<string, boolean>>({});
  const [addSubmitted, setAddSubmitted] = useState(false);
  const [creating, setCreating] = useState(false);

  const selected = staff.find((s) => s.id === selectedId);
  const isDirty = !!selected && modules.some((m) => !!draft[m.name] !== !!selected.permissions[m.name]);

  useEffect(() => {
    updateMainHeader("Staff & Permissions");
    breadCrumbsStore.updateBreadCrumbs([{ Settings: "/settings/general" }, { "Staff & Permissions": "/settings/staff" }]);
  }, []);

  // keepId: stay on this person after a reload (e.g. after saving)
  const load = useCallback(async (keepId?: string) => {
    setLoadError("");
    try {
      const [moduleList, staffList] = await Promise.all([fetchStaffModules(), fetchStaff()]);
      setModules(moduleList);
      setStaff(staffList);
      const next = staffList.find((s) => s.id === keepId) || staffList.find((s) => s.isActive) || staffList[0];
      setSelectedId(next?.id || "");
      setDraft(next ? { ...next.permissions } : {});
    } catch (e: any) {
      setLoadError(getErrorMessage(e, "Couldn't load your staff. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Ask before throwing away unsaved switches
  const guard = (proceed: () => void) => (isDirty ? setPending({ kind: "discard", proceed }) : proceed());

  const selectStaff = (member: StaffMember) =>
    guard(() => {
      setSelectedId(member.id);
      setDraft({ ...member.permissions });
    });

  const visibleStaff = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term
      ? staff.filter((s) => `${s.name} ${s.phoneNumber}`.toLowerCase().includes(term))
      : staff;
    // Active staff first, then by name
    return [...list].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
  }, [staff, search]);

  const activeCount = staff.filter((s) => s.isActive).length;

  const setAll = (setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>, value: boolean) =>
    setter(Object.fromEntries(modules.map((m) => [m.name, value])));

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateStaffPermissions(selected.id, toPermissionList(modules, draft));
      showMessage(`Access updated for ${selected.name || "staff member"}`, "success");
      // Keep the switches as saved even if the list endpoint lags behind
      setStaff((prev) => prev.map((s) => (s.id === selected.id ? { ...s, permissions: { ...draft } } : s)));
    } catch (e: any) {
      showMessage(getErrorMessage(e, "Couldn't update permissions"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await deactivateStaff(selected.id);
      showMessage(`${selected.name || "Staff member"} has been deactivated`, "success");
      await load(selected.id);
    } catch (e: any) {
      showMessage(getErrorMessage(e, "Couldn't deactivate the staff member"), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmPending = () => {
    const current = pending;
    setPending(null);
    if (current?.kind === "discard") current.proceed();
    if (current?.kind === "deactivate") handleDeactivate();
  };

  // ---- Add staff ----
  const trimmedName = newName.trim();
  const nameError = !trimmedName ? "Name is required" : "";
  const phoneError = !newPhone
    ? "Mobile number is required"
    : !PHONE_PATTERN.test(newPhone)
      ? "Enter a valid 10-digit mobile number"
      : staff.some((s) => s.isActive && digitsOnly(s.phoneNumber) === newPhone)
        ? "A staff member with this number already exists"
        : "";
  // The API needs a password to create the account; its rules are left to the backend
  const passwordError = !newPassword ? "Password is required" : "";
  const confirmError = confirmPassword !== newPassword ? "Passwords don't match" : "";
  const modulesError = enabledCount(modules, newModules) === 0 ? "Turn on at least one module" : "";

  const openAdd = () =>
    guard(() => {
      setNewName("");
      setNewPhone("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setNewModules({});
      setAddSubmitted(false);
      setAddOpen(true);
    });

  const handleCreate = async () => {
    setAddSubmitted(true);
    if (nameError || phoneError || passwordError || confirmError || modulesError) return;
    setCreating(true);
    try {
      const created: any = await createStaff({
        name: trimmedName,
        phoneNumber: newPhone,
        password: newPassword,
        enabledModules: modules.filter((m) => newModules[m.name]).map((m) => m.name),
      });
      showMessage(`${trimmedName} added. They can sign in with ${newPhone} and the password you set.`, "success");
      setAddOpen(false);
      const createdId = created?.data?.staffId || created?.data?.id || created?.staffId || created?.id;
      await load(createdId ? String(createdId) : undefined);
    } catch (e: any) {
      showMessage(getErrorMessage(e, "Couldn't add the staff member"), "error");
    } finally {
      setCreating(false);
    }
  };

  const allNewState = {
    checked: modules.length > 0 && enabledCount(modules, newModules) === modules.length,
    indeterminate: enabledCount(modules, newModules) > 0 && enabledCount(modules, newModules) < modules.length,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ color: COLORS.muted, fontSize: "0.875rem", mb: 2, px: 0.5 }}>
        Add staff to your {isPharmacy ? "pharmacy" : "lab"} account and choose which sections each person can use.
        Staff sign in with their own mobile number and password.
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ borderRadius: "12px", mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setLoading(true); load(selectedId); }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2, alignItems: "start" }}>
        {/* Staff */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark }}>
              Staff{" "}
              <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500 }}>
                {activeCount} active
              </Box>
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={openAdd}
              disabled={!!loadError || modules.length === 0}
              sx={{ color: COLORS.primary, textTransform: "none", fontWeight: 600 }}
            >
              Add staff
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or mobile number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ "aria-label": "Search by name or mobile number" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: COLORS.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, mb: 1.5 }}
          />
          <Box role="listbox" aria-label="Staff" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {visibleStaff.map((member) => {
              const isSelected = member.id === selectedId;
              return (
                <ButtonBase
                  key={member.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => selectStaff(member)}
                  sx={{
                    display: "block",
                    textAlign: "left",
                    p: 1.5,
                    borderRadius: "12px",
                    border: `1px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                    bgcolor: isSelected ? COLORS.light : "#fff",
                    opacity: member.isActive ? 1 : 0.7,
                    "&:hover": { borderColor: COLORS.primary },
                    "&.Mui-focusVisible": { outline: `2px solid ${COLORS.primary}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }} noWrap>
                      {member.name || "Unnamed"}
                    </Typography>
                    {!member.isActive && (
                      <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#F8D7DA", color: "#D2122E" }} />
                    )}
                  </Box>
                  <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem", mt: 0.25 }}>
                    {member.phoneNumber || "No mobile number"} · {enabledCount(modules, member.permissions)} of {modules.length} modules
                  </Typography>
                </ButtonBase>
              );
            })}
            {visibleStaff.length === 0 && (
              <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", textAlign: "center", py: 2 }}>
                {staff.length === 0 ? "No staff yet. Use Add staff to create the first account." : "No staff match your search."}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Selected staff member: module access */}
        {selected ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            {!selected.isActive && (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                This account is deactivated, so it can't sign in. Its access is shown for reference.
              </Alert>
            )}

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
              <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark }}>
                {selected.name || "Unnamed"}
              </Typography>
              <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem" }}>
                {selected.phoneNumber || "No mobile number"}
                {selected.createdAt && !isNaN(new Date(selected.createdAt).getTime())
                  ? ` · Added ${new Date(selected.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                  : ""}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                <Typography component="h2" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Module access{" "}
                  <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500, fontSize: "0.85rem" }}>
                    {enabledCount(modules, draft)} of {modules.length} on
                  </Box>
                </Typography>
                {selected.isActive && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" onClick={() => setAll(setDraft, true)} sx={{ color: COLORS.primary, textTransform: "none" }}>
                      Turn all on
                    </Button>
                    <Button size="small" onClick={() => setAll(setDraft, false)} sx={{ color: COLORS.muted, textTransform: "none" }}>
                      Turn all off
                    </Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {modules.map((module) => (
                  <Box
                    key={module.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1,
                      borderTop: `1px solid ${COLORS.light}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: COLORS.dark, fontSize: "0.9rem", fontWeight: 500 }}>{module.label}</Typography>
                      {module.description && (
                        <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>{module.description}</Typography>
                      )}
                    </Box>
                    <Switch
                      checked={!!draft[module.name]}
                      disabled={!selected.isActive || saving}
                      onChange={(e) => setDraft((prev) => ({ ...prev, [module.name]: e.target.checked }))}
                      inputProps={{ "aria-label": `${module.label} access` }}
                      sx={switchSx}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>

            {selected.isActive && (
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, pb: 2 }}>
                <Button
                  color="error"
                  disabled={saving}
                  onClick={() => setPending({ kind: "deactivate" })}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Deactivate staff
                </Button>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    disabled={!isDirty || saving}
                    onClick={() => setDraft({ ...selected.permissions })}
                    sx={{ color: COLORS.muted, textTransform: "none", fontWeight: 600 }}
                  >
                    Discard changes
                  </Button>
                  <Button variant="contained" disableElevation disabled={!isDirty || saving} onClick={handleSave} sx={primaryButtonSx}>
                    {saving ? "Saving..." : "Save changes"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          !loadError && (
            <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
              <Typography sx={{ color: COLORS.muted, fontSize: "0.9rem" }}>
                Add a staff member to choose what they can access.
              </Typography>
            </Paper>
          )
        )}
      </Box>

      {/* Add staff */}
      <Dialog open={addOpen} onClose={() => !creating && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>Add staff</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, pt: 1 }}>
            <TextField
              size="small"
              label="Full name *"
              value={newName}
              onChange={(e) => setNewName(e.target.value.slice(0, NAME_MAX))}
              error={addSubmitted && !!nameError}
              helperText={addSubmitted && nameError ? nameError : " "}
              autoFocus
              sx={fieldSx}
            />
            <TextField
              size="small"
              label="Mobile number *"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              error={addSubmitted && !!phoneError}
              helperText={addSubmitted && phoneError ? phoneError : "Used to sign in"}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
              sx={fieldSx}
            />
            <TextField
              size="small"
              label="Password *"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={addSubmitted && !!passwordError}
              helperText={addSubmitted && passwordError ? passwordError : " "}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            <TextField
              size="small"
              label="Confirm password *"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={addSubmitted && !!confirmError}
              helperText={addSubmitted && confirmError ? confirmError : " "}
              sx={fieldSx}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
            <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "0.9rem" }}>Module access</Typography>
            <FormControlLabel
              label="All modules"
              control={
                <Checkbox
                  size="small"
                  checked={allNewState.checked}
                  indeterminate={allNewState.indeterminate}
                  onChange={(e) => setAll(setNewModules, e.target.checked)}
                  sx={checkboxSx}
                />
              }
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
            />
          </Box>
          {addSubmitted && modulesError && (
            <Typography sx={{ color: "#D32F2F", fontSize: "0.8rem" }}>{modulesError}</Typography>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 2 }}>
            {modules.map((module) => (
              <FormControlLabel
                key={module.name}
                label={module.label}
                control={
                  <Checkbox
                    size="small"
                    checked={!!newModules[module.name]}
                    onChange={(e) => setNewModules((prev) => ({ ...prev, [module.name]: e.target.checked }))}
                    sx={checkboxSx}
                  />
                }
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem", color: COLORS.dark } }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={creating} sx={{ textTransform: "none", color: COLORS.muted }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation disabled={creating} onClick={handleCreate} sx={primaryButtonSx}>
            {creating ? "Adding..." : "Add staff"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm: discard unsaved switches / deactivate */}
      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          {pending?.kind === "deactivate" ? "Deactivate staff?" : "Discard unsaved changes?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem" }}>
            {pending?.kind === "deactivate"
              ? `${selected?.name || "This staff member"} won't be able to sign in any more.`
              : "Your changes to this staff member's access haven't been saved."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPending(null)} sx={{ textTransform: "none", color: COLORS.muted }}>
            {pending?.kind === "deactivate" ? "Cancel" : "Keep editing"}
          </Button>
          <Button onClick={confirmPending} color="error" variant="contained" disableElevation sx={{ textTransform: "none", borderRadius: "10px" }}>
            {pending?.kind === "deactivate" ? "Deactivate" : "Discard"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffPermissions;
